import React, { useState, useEffect } from "react";

import {
  Grid,
  Box,
  Typography,
  IconButton,
  Modal,
  Divider,
  Pagination,
  TextField,
  Checkbox,
  InputAdornment,
  Button,
  CircularProgress,
} from "@mui/material";
import { Visibility, Close, Search, Undo, Save } from "@mui/icons-material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import BoltIcon from "@mui/icons-material/Bolt";
import moment from "moment";
import axios from "axios";

import { IMAGE_BASE_URL, API_BASE_URL } from "../../config/constants";
import TextInput from "../../utility-v1/TextInput";

const SalesRecord = ({
  salesData,
  rowsPerPage = 10,
  selectedShipments = [],
  onSelectShipment,
  onRevert,
  onShipmentUpdate,
}) => {
  const [page, setPage] = useState(1);
  const [selectedSale, setSelectedSale] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [searchName, setSearchName] = useState("");
  const [shipmentDetails, setShipmentDetails] = useState({});
  const [autoLoadingId, setAutoLoadingId] = useState(null);

  useEffect(() => {
    const initialShipmentDetails = {};
    salesData.forEach((sale) => {
      initialShipmentDetails[sale.sales_id] = {
        shipmentPrice: sale.shipment_price || "", // ✅ Use existing shipment price
        shipmentMethod: sale.shipment_method || "",
        trackingId: sale.tracking_id || "",
      };
    });
    setShipmentDetails(initialShipmentDetails);
  }, [salesData]); // ✅ Runs when salesData changes

  // ✅ Fix: Check if `onSelectShipment` is provided before calling
  const handleSelectShipment = (salesId) => {
    if (onSelectShipment) {
      onSelectShipment(salesId); // ✅ Ensure the function is being called
    } else {
      console.error("onSelectShipment is not defined!");
    }
  };

  const handleShipmentChange = (salesId, field, value) => {
    setShipmentDetails((prev) => ({
      ...prev,
      [salesId]: {
        ...prev[salesId],
        [field]: value || "", // ✅ Ensure value is not undefined
      },
    }));
  };

  // Handle shipment price update
  const handleSaveShipment = (salesId) => {
    if (shipmentDetails[salesId]) {
      onShipmentUpdate(salesId, shipmentDetails[salesId]);
    }
  };

  // Name search
  const filteredSales = salesData.filter((sale) =>
    sale.name.toLowerCase().includes(searchName.toLowerCase())
  );

  // Pagination
  const totalPages = Math.ceil(filteredSales.length / rowsPerPage);
  const displayedSales = filteredSales.slice(
    (page - 1) * rowsPerPage,
    page * rowsPerPage
  );

  // Filter toggle
  const toggleFilter = () => {
    if (filterOpen) {
      setSearchName(""); // Clear search
      setPage(1); // Reset pagination
    }
    setFilterOpen(!filterOpen);
  };

  // Row color by status
  const getStatusColor = (status) => {
    switch (status) {
      case "SP":
        return "#FFECB3"; // Light Orange
      case "SD":
        return "#DFF2BF"; // Light Green
      case "SR":
        return "#E0E0E0"; // Light Gray
      default:
        return "#f8f9fa"; // Default Gray
    }
  };

  // 🌟 Simple Auto-quote using fixed params on server (200g, origin 695029)
  const quickFillShipment = async (sale) => {
    try {
      setAutoLoadingId(sale.sales_id);
      const dPin = String(sale.pincode || "").trim();

      if (!/^[1-9]\d{5}$/.test(dPin)) {
        alert("Invalid destination pincode");
        return;
      }

      const { data } = await axios.get(
        `${API_BASE_URL}/shipping/delhivery/quick-quote`,
        { params: { d_pin: dPin }, timeout: 15000 }
      );

      if (data?.ok && data.amount != null) {
        setShipmentDetails((prev) => ({
          ...prev,
          [sale.sales_id]: {
            ...prev[sale.sales_id],
            shipmentPrice: Number(data.amount),
            shipmentMethod: ")", // optional label
          },
        }));
      } else {
        alert("Could not fetch price");
      }
    } catch (e) {
      console.error(e);
      alert(e?.response?.data?.details?.message || e.message || "Quote error");
    } finally {
      setAutoLoadingId(null);
    }
  };

  return (
    <Box sx={{ position: "relative" }}>
      {/* Filter Icon - Absolute Positioned */}
      <IconButton
        onClick={toggleFilter}
        sx={{
          position: "absolute",
          left: -5,
          top: -5,
        }}
      >
        {filterOpen ? <Close /> : <Search />}
      </IconButton>

      {/* Pagination */}
      <Grid container justifyContent="right" sx={{ mb: 2 }}>
        <Pagination
          count={totalPages}
          page={page}
          onChange={(event, value) => setPage(value)}
          color="primary"
          size="small"
          sx={{
            "& .MuiPagination-ul": { justifyContent: "center" },
            "& .MuiPaginationItem-root": { fontSize: "16px" },
          }}
        />
      </Grid>

      {/* Filter Section */}
      {filterOpen && (
        <Box sx={{ mb: 2 }}>
          <TextInput
            label="Search by Name"
            name="searchName"
            value={searchName}
            onChange={(e) => setSearchName(e.target.value)}
            placeholder="Search by name"
            type="text"
          />
        </Box>
      )}

      {/* Sales Records */}
      <Grid container spacing={1}>
        {displayedSales.map((sale) => (
          <Grid item xs={12} key={sale.sales_id}>
            <Box
              sx={{
                backgroundColor: getStatusColor(sale.sales_status),
                paddingLeft: "10px",
                paddingTop: "10px",
                paddingRight: "10px",
                paddingBottom: "5px",
                borderRadius: 1,
                border: "1px solid #ccc",
              }}
            >
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography sx={{ fontWeight: "bold" }}>{sale.name}</Typography>
                <Typography
                  sx={{ fontWeight: "bold", color: "#888", fontSize: "14px" }}
                >
                  {moment(sale.sales_date).format("DD/MM/YYYY")}
                </Typography>
              </Box>

              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mt: 0 }}
              >
                <Typography sx={{ fontWeight: "bold" }}>
                  ₹{sale.total_price}
                </Typography>

                <Typography
                  component="a"
                  href={`https://wa.me/91${sale.phone_number}?text=${encodeURIComponent(
                    "Hi, Your order has been shipped."
                  )}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    fontWeight: "bold",
                    color: "#25D366",
                    cursor: "pointer",
                    textDecoration: "underline",
                    display: "flex",
                    alignItems: "center",
                    gap: "5px",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <WhatsAppIcon sx={{ fontSize: 20 }} /> {sale.phone_number}
                </Typography>

                <IconButton onClick={() => setSelectedSale(sale)}>
                  <Visibility />
                </IconButton>

                {sale.sales_status !== "SP" && (
                  <IconButton onClick={() => onRevert(sale.sales_id)}>
                    <Undo />
                  </IconButton>
                )}
              </Box>

              {/* ✅ Hide shipment section if status is 'SD' */}
              {sale.sales_status !== "SD" && (
                <Box display="flex" alignItems="center" sx={{ mt: 1, gap: 1 }}>
                  <Checkbox
                    checked={selectedShipments.includes(sale.sales_id)}
                    onChange={() => handleSelectShipment(sale.sales_id)}
                  />

                  <TextField
                    label="Shipment Price"
                    type="number"
                    value={
                      shipmentDetails[sale.sales_id]?.shipmentPrice !==
                      undefined
                        ? shipmentDetails[sale.sales_id]?.shipmentPrice
                        : sale.shipment_price || ""
                    }
                    onChange={(e) =>
                      handleShipmentChange(
                        sale.sales_id,
                        "shipmentPrice",
                        e.target.value
                      )
                    }
                    placeholder="Enter shipment price"
                    sx={{
                      backgroundColor:
                        shipmentDetails[sale.sales_id]?.shipmentPrice ||
                        sale.shipment_price
                          ? "#DFF2BF"
                          : "white",
                      borderRadius: "5px",
                      minWidth: 220,
                    }}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={
                              autoLoadingId === sale.sales_id ? null : (
                                <BoltIcon />
                              )
                            }
                            onClick={() => quickFillShipment(sale)}
                            disabled={autoLoadingId === sale.sales_id}
                          >
                            {autoLoadingId === sale.sales_id ? (
                              <CircularProgress size={16} />
                            ) : (
                              "Auto"
                            )}
                          </Button>
                        </InputAdornment>
                      ),
                    }}
                  />

                  <IconButton
                    color="primary"
                    onClick={() => handleSaveShipment(sale.sales_id)}
                  >
                    <Save />
                  </IconButton>
                </Box>
              )}
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Modal for Sales Details */}
      <Modal open={!!selectedSale} onClose={() => setSelectedSale(null)}>
        <Box
          sx={{
            backgroundColor: "white",
            p: 3,
            maxWidth: 450,
            margin: "auto",
            mt: 5,
            borderRadius: 2,
            position: "relative",
          }}
        >
          <IconButton
            onClick={() => setSelectedSale(null)}
            sx={{ position: "absolute", top: 8, right: 8 }}
          >
            <Close />
          </IconButton>

          <Typography variant="h6" sx={{ mb: 2 }}>
            {selectedSale?.name}
          </Typography>

          <Divider sx={{ my: 1 }} />

          <Typography sx={{ fontWeight: "bold" }}>Buyer Details:</Typography>
          <Typography>{selectedSale?.buyer_details}</Typography>
          <Typography>
            {selectedSale?.state}, {selectedSale?.pincode}
          </Typography>
          <Typography>Phone: {selectedSale?.phone_number}</Typography>
          <Typography>
            Shipment Price: ₹{selectedSale?.shipment_price}
          </Typography>

          <Divider sx={{ my: 1 }} />

          <Typography sx={{ fontWeight: "bold" }}>Items:</Typography>
          <Box sx={{ display: "flex", overflowX: "auto", gap: 2, mt: 1, p: 1 }}>
            {selectedSale?.items.map((item, index) => (
              <Box key={index} sx={{ minWidth: 120, textAlign: "center" }}>
                {item.image && (
                  <img
                    src={`${IMAGE_BASE_URL}${item.image}`}
                    alt={item.item_label}
                    style={{
                      width: 100,
                      height: 100,
                      objectFit: "cover",
                      borderRadius: 8,
                    }}
                  />
                )}
                <Typography sx={{ fontSize: "14px", mt: 1 }}>
                  {item.item_label}
                </Typography>
              </Box>
            ))}
          </Box>
        </Box>
      </Modal>
    </Box>
  );
};

export default SalesRecord;
